import { Directive, inject, input, OnInit } from '@angular/core';
import { AuthService } from '@app/core/services/auth.service';
import { LocationService } from '@app/core/services/location.service';
import { MultiSelect } from 'primeng/multiselect';
import { concatMap, map, groupBy, mergeMap, zip, of, toArray, switchMap } from 'rxjs';

@Directive({
  selector: '[appLocationMultiselect]',
  exportAs: 'locationMultiselectInstance',
  standalone: true
})
export class LocationMultiselectDirective implements OnInit {
  private _hostComponent = inject(MultiSelect);
  private _locationService = inject(LocationService);
  private _authService = inject(AuthService);

  onlyUserCountry = input<boolean>(false);

  load$ = of(true).pipe(
    switchMap(() => {
      if (this.onlyUserCountry() && this._authService.IsFranchiseeUser) {
        const countryUid = this._authService.Country;
        if (!countryUid) {
          throw new Error('Country is not defined for the current user');
        }
        return this._locationService.getAllByCountry(countryUid);
      } else {
        return this._locationService.getAllLocations();
      }
    }),
    concatMap((arr) => arr),
    map((x) => {
      const label = `${x.referenceCode} - ${x.name}`;
      return {
        code: x.referenceCode,
        optionLabel: label,
        countryCode: x.countryCode,
        countryName: x.countryName,
        uid: x.uid
      };
    }),
    groupBy((loc) => loc.countryCode.toLocaleLowerCase()),
    mergeMap((group) => zip(of(group.key), group.pipe(toArray()))),
    map(([key, items]) => ({
      countryName: items[0].countryName,
      countryCode: items[0].countryCode,
      flagIcon: `fi fi-${key}`,
      items: items
    })),
    toArray()
  );

  ngOnInit(): void {
    this.load$.subscribe((locations) => (this._hostComponent.options = locations));
  }

  selectCountry(countryCode: string) {
    if (this._hostComponent.options) {
      const locationsInCountry = this._hostComponent.options
        .filter((group) =>
          group.items.some(
            (loc: unknown) => (loc as { countryCode: string }).countryCode.toLowerCase() === countryCode.toLowerCase()
          )
        )
        .flatMap((group) => group.items);

      this._hostComponent.updateModel(locationsInCountry);
    }
  }
}
